/**
 * Stored-file reference → the one signed-in file route (story 25240).
 *
 * The substrate emits `/api/files/<source>/…` references for UI calls; those pass
 * through. Storage URLs that still arrive (chat links from ChatAI, old cached
 * data, anything the substrate rewrite missed) map by CONTAINER — the host is
 * ignored because the API reads its own configured account per source. Legacy
 * `/file/getfile?fileName=…` hrefs are unwrapped. Documents are served by id
 * only (`document/<id>`), so a raw documents-container URL does not map.
 * Must match Product.Application/Files/FileSources.cs.
 *   ora     → Remix route api.files.$.tsx (session cookie) → Product.Api
 *   add-in  → server.mjs relay (bearer) → Product.Api
 */
export const FILES_PREFIX = "/api/files/";

const SOURCE_BY_CONTAINER: Readonly<Record<string, string>> = {
  "rpa-artifacts": "workflow-artifacts",
  "orachat-files": "chat-files",
  "ora-profile-pictures": "profile-pictures",
};
const SOURCES = new Set(["workflow-artifacts", "document", "chat-files", "profile-pictures"]);
const BLOB_HOST_SUFFIX = ".blob.core.windows.net";
const AZURITE_ACCOUNT = "devstoreaccount1";
const LEGACY_GETFILE_RE = /\/(?:api\/)?file\/getfile/i;
const MAX_LEGACY_NESTING = 2;
// A backslash or C0/DEL control char is dropped or rewritten by URL parsers (`\` -> `/`, tab/CR/LF removed),
// which turns an innocent-looking segment into a real `..`. Reject such input outright.
// eslint-disable-next-line no-control-regex
const UNSAFE_CHARS_RE = /[\\\u0000-\u001f\u007f]/;
const DOT_SEGMENT_RE = /^(?:\.|%2e){1,2}$/i;

function parseBlobUrl(url: string): { container: string; blob: string[] } | null {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return null;
  }
  if (parsed.protocol !== "https:" && parsed.protocol !== "http:") return null;
  let segments = parsed.pathname.split("/").filter((s) => s.length > 0);
  if (parsed.hostname.toLowerCase().endsWith(BLOB_HOST_SUFFIX)) {
    // https://<account>.blob.core.windows.net/<container>/<blob…>
  } else if (segments[0] === AZURITE_ACCOUNT) {
    segments = segments.slice(1); // Azurite path style
  } else {
    return null;
  }
  if (segments.length < 2) return null;
  return { container: segments[0], blob: segments.slice(1) };
}

export function toFileRoutePath(url: string | null | undefined, depth = 0): string | null {
  if (!url || depth > MAX_LEGACY_NESTING || UNSAFE_CHARS_RE.test(url)) return null;

  if (url.startsWith(FILES_PREFIX)) {
    const path = url.split(/[?#]/, 1)[0];
    const segments = path.slice(FILES_PREFIX.length).split("/");
    const ok =
      segments.length >= 2 &&
      SOURCES.has(segments[0]) &&
      segments.every((s) => s.length > 0 && !DOT_SEGMENT_RE.test(s));
    return ok ? path : null;
  }

  // A real storage URL wins over the legacy unwrap: a blob path may itself contain "/file/getfile".
  const blob = parseBlobUrl(url);
  if (blob) {
    const source = SOURCE_BY_CONTAINER[blob.container];
    return source ? `${FILES_PREFIX}${source}/${blob.blob.join("/")}` : null;
  }

  if (LEGACY_GETFILE_RE.test(url)) {
    let inner: string | null;
    try {
      inner = new URL(url, "https://legacy.invalid").searchParams.get("fileName");
    } catch {
      return null;
    }
    return toFileRoutePath(inner, depth + 1);
  }
  return null;
}

export function isStoredFileUrl(url: string | null | undefined): boolean {
  return toFileRoutePath(url) !== null;
}

/** Any storage URL, mapped or not. One that doesn't map is shown as unavailable, never linked. */
export function isBlobStorageUrl(url: string | null | undefined): boolean {
  if (!url) return false;
  try {
    const parsed = new URL(url);
    return parsed.hostname.toLowerCase().endsWith(BLOB_HOST_SUFFIX) || parsed.pathname.startsWith(`/${AZURITE_ACCOUNT}/`);
  } catch {
    return false;
  }
}

/**
 * A reference that looks like a stored file but does not map: a storage URL with
 * no source, a legacy getfile href that unwraps to nothing, or an unknown
 * /api/files/ path. Shown as unavailable, never loaded or linked.
 */
export function isUnavailableStoredUrl(url: string | null | undefined): boolean {
  if (!url) return false;
  if (UNSAFE_CHARS_RE.test(url)) return true;
  if (toFileRoutePath(url) !== null) return false;
  return isBlobStorageUrl(url) || LEGACY_GETFILE_RE.test(url) || url.startsWith(FILES_PREFIX);
}

/** Decoded last path segment — the name for downloads and labels. */
export function storedFileName(url: string): string {
  const path = toFileRoutePath(url) ?? url.split(/[?#]/, 1)[0];
  const last = path.split("/").pop() ?? "";
  try {
    return decodeURIComponent(last);
  } catch {
    return last;
  }
}

/** A readable label for a stored-file link: the file name, or "Document" for `document/<id>` references. */
export function storedFileLabel(url: string): string {
  const path = toFileRoutePath(url);
  if (path?.startsWith(`${FILES_PREFIX}document/`)) return "Document";
  return storedFileName(url);
}
